import { TFormatted } from '@ws/shared/types';
import { toast } from 'react-toastify';

import { downloadBlob } from './downloadBlob';
import { hasRenderableContent } from './hasRenderableContent';
import { sanitizeFileName } from './sanitizeFileName';

export type TPdfModule = typeof import('./pdf/buildPdf');
export type TPdfModuleLoader = () => Promise<TPdfModule>;

/**
 * Точка разделения бандла: pdfmake и html-to-pdfmake живут за этим import() и попадают
 * в отдельный чанк, который грузится только при первом экспорте.
 */
const defaultLoader: TPdfModuleLoader = () =>
  import(/* webpackChunkName: "pdf-export" */ './pdf/buildPdf');

/**
 * Собирает и отдаёт .pdf по скомпилированным данным коллекции.
 *
 * Устройство зеркалит docx-экспорт: данные принимаются функцией, чтобы компиляция
 * коллекции попала внутрь той же границы ошибок, а скачивание идёт через общий
 * downloadBlob. Пустая коллекция даёт сообщение, а не пустой файл.
 *
 * Любой сбой — компиляция, загрузка чанка, генерация, таймаут, скачивание —
 * заканчивается тостом.
 */
export async function exportPdf(
  getEntities: () => TFormatted[],
  nameForFile: string,
  loadPdfModule: TPdfModuleLoader = defaultLoader,
): Promise<void> {
  try {
    const entities = getEntities();

    if (!hasRenderableContent(entities)) {
      toast.error('В этой коллекции нечего экспортировать.');

      return;
    }

    const { buildPdfDefinition, pdfToBlob } = await loadPdfModule();
    const blob = await pdfToBlob(buildPdfDefinition(entities));

    downloadBlob(blob, `${sanitizeFileName(nameForFile)}.pdf`);
  } catch (error) {
    console.error('Не удалось собрать pdf:', error);
    toast.error('Не удалось сгенерировать pdf файл. Попробуйте позже.');
  }
}
